import { validateImageFile } from '@/domain/use-cases/transform-image';
import { readAndCompressImage } from 'browser-image-resizer';
import React, { useEffect, useState } from 'react';
import { MdImage } from 'react-icons/md';
import { toast } from 'react-toastify';

interface ImageUploadProps {
  onFileChange: (file: File | null) => void;
  id: string;
  image?: string | null;
}

const MAX_DIMENSION_PX = 1600;
const JPEG_QUALITY = 0.8;
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

const ImageUpload: React.FC<ImageUploadProps> = ({
  onFileChange,
  image,
  id,
}) => {
  const [previewURL, setPreviewURL] = useState<string | null>(image || null);

  // The `image` prop only carries the *initial* value in via useState above
  // — it doesn't otherwise participate in this component's own render.
  // But a parent can also set it later (e.g. restoring a draft assigns a
  // File to its own state and derives a data URL for this prop), and
  // without this, the preview silently stays on the empty placeholder even
  // though the restored file is genuinely attached (visible only once you
  // reach a later step that reads the file state directly).
  useEffect(() => {
    setPreviewURL(image || null);
  }, [image]);

  const compressImage = async (file: File): Promise<File> => {
    const blob = await readAndCompressImage(file, {
      quality: JPEG_QUALITY,
      maxWidth: MAX_DIMENSION_PX,
      maxHeight: MAX_DIMENSION_PX,
      mimeType: 'image/jpeg',
    });

    return new File([blob], file.name, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  };

  const validateImageSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert('La imagen no debe superar los 2MB.');
      return false;
    }
    return true;
  };

  const processImage = async (file: File) => {
    try {
      if (!validateImageFile(file)) {
        toast.error('Por favor, selecciona una imagen válida (JPG o PNG).');
        onFileChange(null);
        return;
      }

      setPreviewURL(URL.createObjectURL(file));

      toast.info('Comprimiendo imagen...');
      const compressedFile = await compressImage(file);

      if (!validateImageSize(compressedFile)) {
        onFileChange(null);
        return;
      }

      setPreviewURL(URL.createObjectURL(compressedFile));
      onFileChange(compressedFile);

      const reduction = (
        ((file.size - compressedFile.size) / file.size) *
        100
      ).toFixed(0);
      toast.success(`Imagen comprimida (reducción: ${reduction}%)`);
    } catch (error) {
      console.error('Error al procesar la imagen:', error);
      toast.error('Error al procesar la imagen. Por favor, intenta con otra.');
      onFileChange(null);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await processImage(file);
    } else {
      alert('Por favor, selecciona una imagen.');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await processImage(file);
    } else {
      alert('Por favor, arrastra una imagen.');
    }
  };

  return (
    <div
      className="flex flex-col items-center space-y-4"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 w-full bg-white hover:border-blue-500 transition-colors">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id={id}
        />
        <label
          htmlFor={id}
          className="flex flex-col items-center space-y-4 cursor-pointer"
        >
          {previewURL ? (
            <>
              <img
                src={previewURL}
                alt="Preview"
                className="w-60 h-60 object-cover rounded-lg"
              />
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Haz clic para cambiar la imagen
                </p>
              </div>
            </>
          ) : (
            <>
              <MdImage size={60} className="text-gray-500" />
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700">
                  Arrastra y suelta tu imagen aquí
                </p>
                <p className="text-sm text-gray-500">
                  o haz clic para seleccionar
                </p>
                <p className="text-xs text-gray-400">Tamaño máximo: 2MB</p>
                <p className="text-xs text-blue-500 mt-1">
                  Se comprimirá automáticamente para reducir espacio
                </p>
              </div>
            </>
          )}
        </label>
      </div>
    </div>
  );
};

export default ImageUpload;
